import { FieldValues } from "react-hook-form";

import MenuPageLayout from "@/components/layouts/MenuPageLayout";
import { request } from "@/utils/request";
import { useAppContext } from "@/contexts/app-context";
import { useCollection, useResource } from "@/hooks/resources";
import { PlayOptionsForm } from "@/components/forms/play-options-form";
import { Tab, Tabs } from "@/components/tabs";
import { LoadingText } from "@/components/loading-text";
import SongPreference from "@/types/song-preference";
import { Button } from "@/components/button";
import { useState } from "react";
import clsx from "clsx";

const SongPreferenceStatus = ({ songPreference }: { songPreference: SongPreference }) => {
  if (!songPreference) {
    return null;
  } else if (songPreference.preAssessmentCompleted) {
    return "Pre-Assessment Completed"
  } else if (songPreference.postAssessmentCompleted) {
    return "Post-Assessment Completed"
  } else if (songPreference.songStarted) {
    return "Song Started"
  }

  const { assessmentEnabled } = songPreference;

  return (
    <span className={clsx("border rounded-lg px-2 py-0.5", assessmentEnabled ? "border-primary-green-500" : "border-red-500")}>
      {assessmentEnabled ? "Enabled" : "Disabled"}
    </span>
  );
};

const SettingsPage = () => {
  const {
    state: { currentUser, selectedChildId },
  } = useAppContext();

  const [userPreference, { isLoading }] = useResource<FieldValues>(
    `/user_preference?child_id=${selectedChildId}`,
    {},
  );

  const [songPreferences, { isLoading: isLoadingSongPreferences, refetch: refetchSongPreferences }] = useCollection<SongPreference>(
    `/song_preferences?child_id=${selectedChildId}`,
    {
      enabled: selectedChildId !== undefined,
    },
  );

  const onSubmit = async (data: FieldValues) => {
    await request.put(`/user_preference?child_id=${selectedChildId}`, data);
  };

  const [updatingSongPreferenceId, setUpdatingSongPreferenceId] = useState<number>();

  const updateSongPreference = async (id: number, assessmentEnabled: boolean) => {
    setUpdatingSongPreferenceId(id);
    await request.put(`/song_preferences/${id}`, { assessmentEnabled });
    await refetchSongPreferences();
    setUpdatingSongPreferenceId(undefined);
  };

  return (
    <MenuPageLayout title="Settings">
      <div className="w-full max-w-screen-lg flex flex-col gap-8">
        <Tabs>
          <Tab label="Play options" value="play-options">
            {!isLoading && userPreference && (
              <PlayOptionsForm
                defaultValues={{
                  playbackSpeed: userPreference.playbackSpeed.toFixed(1),
                  showRewards: userPreference.showRewards.toString(),
                  showVocabularyText: userPreference.showVocabularyText.toString(),
                }}
                onSubmit={onSubmit}
              />
            )}
          </Tab>

          {
            currentUser?.entitlements.includes("assessments") && (
              <Tab label="Pre and Post-Assessment Options" value="pre_post_assessment_options">
                <div className="p-4 flex flex-col gap-4">
                  <div className="flex items-center justify-between border-b pb-4 h-[65px]">
                    <h2 className="text-3xl">Pre and Post-Assessment Options</h2>
                  </div>

                  {
                    selectedChildId ? (
                      <>
                        <p>On any song you haven&apos;t started yet, you can enable or disable <em>Pre and Post-Assessment</em>.</p>

                        <div className="border rounded-lg overflow-clip">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-primary-orange-200 divide-x divide-primary-orange-100">
                                <th className="p-2 ps-4 text-start">Song</th>
                                <th className="p-2 text-start">Pre and Post-Assessment</th>
                                <th />
                              </tr>
                            </thead>

                            <tbody>
                              {
                                (songPreferences || []).map(songPreference => (
                                  <tr className="odd:bg-gray-100 divide-x" key={songPreference.id}>
                                    <td className="p-2 ps-4">{songPreference.song.name}</td>
                                    <td className="p-2">
                                      <SongPreferenceStatus songPreference={songPreference} />
                                    </td>
                                    <td className="p-2">
                                      {
                                        !songPreference.songStarted && (
                                          songPreference.assessmentEnabled ? (
                                            <Button
                                              type="button"
                                              className={clsx("link", { "disabled:text-gray-400": updatingSongPreferenceId !== songPreference.id })}
                                              onClick={() => updateSongPreference(songPreference.id, false)}
                                              loading={updatingSongPreferenceId === songPreference.id}
                                              disabled={!!updatingSongPreferenceId}
                                            >
                                              Disable
                                            </Button>
                                          ) : (
                                            <Button
                                              type="button"
                                              className={clsx("link", { "disabled:text-gray-400": updatingSongPreferenceId !== songPreference.id })}
                                              onClick={() => updateSongPreference(songPreference.id, true)}
                                              loading={updatingSongPreferenceId === songPreference.id}
                                              disabled={!!updatingSongPreferenceId}
                                            >
                                              Enable
                                            </Button>
                                          )
                                        )
                                      }
                                    </td>
                                  </tr>
                                ))
                              }
                            </tbody>
                          </table>

                          {
                            selectedChildId && isLoadingSongPreferences && (
                              <div className="p-4 flex justify-center items-center">
                                <LoadingText />
                              </div>
                            )
                          }
                        </div>
                      </>
                    ) : (
                      <p>Select a child to configure Pre and Post-Assessment options.</p>
                    )
                  }
                </div>
              </Tab>
            )
          }
        </Tabs>
      </div>
    </MenuPageLayout>
  );
};

export default SettingsPage;
